import React, { useState } from 'react';
import {
  Box, Heading, Container, Table, Thead, Tbody, Tr, Th, Td, Badge, HStack,
  Select, Input, Button, Flex, Text, Card, CardBody, useToast, VStack,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, FormControl, FormLabel, useDisclosure, Textarea,
  Stat, StatLabel, StatNumber, StatGroup, Alert, AlertIcon,
} from '@chakra-ui/react';
import { FiPlus } from 'react-icons/fi';

const CATEGORIES = [
  'Infraestrutura',
  'Serviços (APIs)',
  'Marketing',
  'Pessoal',
  'Jurídico',
  'Impostos',
  'Outros',
];

const STATUS_COLORS = { pago: 'green', pendente: 'yellow', vencido: 'red' };

const ContasPagar = () => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [expenses, setExpenses] = useState([]);
  const [filter, setFilter] = useState({ category: '', status: '' });

  const [form, setForm] = useState({
    description: '', category: 'Infraestrutura', amount: '', dueDate: '', notes: '',
  });

  const formatCurrency = (val) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const handleAdd = () => {
    if (!form.description || !form.amount || !form.dueDate) {
      return toast({ title: 'Preencha os campos obrigatórios', status: 'warning', duration: 3000, isClosable: true, position: 'top-right' });
    }

    const amount = parseFloat(form.amount.replace(',', '.'));
    if (isNaN(amount) || amount <= 0) {
      return toast({ title: 'Valor inválido', status: 'warning', duration: 3000, isClosable: true, position: 'top-right' });
    }

    const now = new Date();
    const due = new Date(form.dueDate);
    const status = due < now ? 'vencido' : 'pendente';

    const newExpense = {
      id: Date.now().toString(),
      description: form.description,
      category: form.category,
      amount,
      dueDate: form.dueDate,
      status,
      notes: form.notes,
      createdAt: new Date().toISOString(),
    };

    setExpenses(prev => [newExpense, ...prev]);
    setForm({ description: '', category: 'Infraestrutura', amount: '', dueDate: '', notes: '' });
    onClose();
    toast({ title: 'Despesa adicionada', status: 'success', duration: 2000, isClosable: true, position: 'top-right' });
  };

  const togglePaid = (id) => {
    setExpenses(prev => prev.map(e =>
      e.id === id ? { ...e, status: e.status === 'pago' ? 'pendente' : 'pago' } : e
    ));
  };

  const filtered = expenses.filter(e => {
    if (filter.category && e.category !== filter.category) return false;
    if (filter.status && e.status !== filter.status) return false;
    return true;
  });

  const totals = React.useMemo(() => {
    let total = 0, paid = 0, pending = 0, overdue = 0;
    expenses.forEach(e => {
      total += e.amount;
      if (e.status === 'pago') paid += e.amount;
      else if (e.status === 'vencido') overdue += e.amount;
      else pending += e.amount;
    });
    return { total, paid, pending, overdue };
  }, [expenses]);

  return (
    <Container maxW="container.xl" py={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <VStack align="start" spacing={0}>
          <Heading size="lg">Contas a Pagar</Heading>
          <Text color="gray.500" fontSize="sm">Despesas operacionais</Text>
        </VStack>
        <Button leftIcon={<FiPlus />} colorScheme="teal" size="sm" onClick={onOpen}>
          Nova Despesa
        </Button>
      </Flex>

      <Alert status="info" borderRadius="md" mb={6}>
        <AlertIcon />
        As despesas são armazenadas localmente nesta sessão. Uma futura atualização conectará esta página a um banco de dados para persistência.
      </Alert>

      {/* Summary Cards */}
      <StatGroup mb={6}>
        <Card flex={1} mr={3}><CardBody py={3}>
          <Stat><StatLabel>Total</StatLabel><StatNumber fontSize="lg">{formatCurrency(totals.total)}</StatNumber></Stat>
        </CardBody></Card>
        <Card flex={1} mr={3}><CardBody py={3}>
          <Stat><StatLabel>Pago</StatLabel><StatNumber fontSize="lg" color="green.500">{formatCurrency(totals.paid)}</StatNumber></Stat>
        </CardBody></Card>
        <Card flex={1} mr={3}><CardBody py={3}>
          <Stat><StatLabel>Pendente</StatLabel><StatNumber fontSize="lg" color="yellow.600">{formatCurrency(totals.pending)}</StatNumber></Stat>
        </CardBody></Card>
        <Card flex={1}><CardBody py={3}>
          <Stat><StatLabel>Vencido</StatLabel><StatNumber fontSize="lg" color="red.500">{formatCurrency(totals.overdue)}</StatNumber></Stat>
        </CardBody></Card>
      </StatGroup>

      {/* Filters */}
      <Card mb={4}>
        <CardBody py={3}>
          <HStack spacing={3}>
            <Select size="sm" maxW="180px" placeholder="Todas categorias" value={filter.category}
              onChange={(e) => setFilter(f => ({ ...f, category: e.target.value }))}>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </Select>
            <Select size="sm" maxW="150px" placeholder="Todos os status" value={filter.status}
              onChange={(e) => setFilter(f => ({ ...f, status: e.target.value }))}>
              <option value="pago">Pago</option>
              <option value="pendente">Pendente</option>
              <option value="vencido">Vencido</option>
            </Select>
            {(filter.category || filter.status) && (
              <Button size="sm" variant="ghost" onClick={() => setFilter({ category: '', status: '' })}>Limpar</Button>
            )}
          </HStack>
        </CardBody>
      </Card>

      {/* Table */}
      <Card>
        <CardBody p={0}>
          {filtered.length === 0 ? (
            <Flex justify="center" py={10}>
              <Text color="gray.500">{expenses.length === 0 ? 'Nenhuma despesa registrada. Clique em "Nova Despesa" para começar.' : 'Nenhum resultado com os filtros aplicados.'}</Text>
            </Flex>
          ) : (
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Descrição</Th>
                    <Th>Categoria</Th>
                    <Th>Vencimento</Th>
                    <Th isNumeric>Valor</Th>
                    <Th>Situação</Th>
                    <Th>Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filtered.map((e) => (
                    <Tr key={e.id} _hover={{ bg: 'gray.50' }}>
                      <Td>
                        <VStack align="start" spacing={0}>
                          <Text fontSize="sm" fontWeight="medium">{e.description}</Text>
                          {e.notes && <Text fontSize="xs" color="gray.500">{e.notes}</Text>}
                        </VStack>
                      </Td>
                      <Td><Badge variant="subtle" fontSize="xs">{e.category}</Badge></Td>
                      <Td fontSize="sm">{formatDate(e.dueDate)}</Td>
                      <Td isNumeric fontWeight="medium" fontSize="sm">{formatCurrency(e.amount)}</Td>
                      <Td>
                        <Badge colorScheme={STATUS_COLORS[e.status] || 'gray'} fontSize="xs">
                          {e.status.charAt(0).toUpperCase() + e.status.slice(1)}
                        </Badge>
                      </Td>
                      <Td>
                        <Button size="xs" variant="ghost" colorScheme={e.status === 'pago' ? 'gray' : 'green'}
                          onClick={() => togglePaid(e.id)}>
                          {e.status === 'pago' ? 'Desfazer' : 'Marcar Pago'}
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>

      {/* Add Expense Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Nova Despesa</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Descrição</FormLabel>
                <Input value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Ex: Servidor Cloudflare R2" />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Categoria</FormLabel>
                <Select value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Valor (R$)</FormLabel>
                <Input value={form.amount} onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))}
                  placeholder="29,90" />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Data de Vencimento</FormLabel>
                <Input type="date" value={form.dueDate} onChange={(e) => setForm(f => ({ ...f, dueDate: e.target.value }))} />
              </FormControl>
              <FormControl>
                <FormLabel>Observações</FormLabel>
                <Textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Opcional" rows={2} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancelar</Button>
            <Button colorScheme="teal" onClick={handleAdd}>Adicionar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default ContasPagar;
